import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useState } from 'react';
import {
  amazonInventoryApi,
  BULK_ACTIONS,
  type AIRecommendation,
  type ActivityEntry,
  type DashboardSummary,
  type DemandForecast,
  type InventoryAlert,
  type InventoryGridRow,
  type InventoryHealthCenter,
  type InventoryItem,
  type ReplenishmentPlan,
  type SearchResult,
  type WarehouseView,
} from '../../api/amazoninventory';

const WORKSPACE = (getWorkspaceId() ?? '');

export function InventoryDashboardPage() {
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [health, setHealth] = useState<InventoryHealthCenter | null>(null);
  const [rows, setRows] = useState<InventoryGridRow[]>([]);
  const [total, setTotal] = useState(0);
  const [warehouses, setWarehouses] = useState<WarehouseView[]>([]);
  const [alerts, setAlerts] = useState<InventoryAlert[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [forecast, setForecast] = useState<DemandForecast | null>(null);
  const [plan, setPlan] = useState<ReplenishmentPlan | null>(null);
  const [recs, setRecs] = useState<AIRecommendation[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [stockoutFilter, setStockoutFilter] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, hc, grid, wh, act, al] = await Promise.all([
        amazonInventoryApi.dashboard(WORKSPACE),
        amazonInventoryApi.healthCenter(WORKSPACE),
        amazonInventoryApi.inventoryGrid(WORKSPACE),
        amazonInventoryApi.warehouseViews(WORKSPACE),
        amazonInventoryApi.listActivity(undefined, 20),
        amazonInventoryApi.listAlerts(true),
      ]);
      setDashboard(dash);
      setHealth(hc);
      setRows(grid.rows);
      setTotal(grid.total);
      setWarehouses(wh);
      setActivity(act);
      setAlerts(al);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load inventory');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runSearch = async () => {
    if (!searchText.trim()) {
      setSearchResult(null);
      return;
    }
    setBusy(true);
    try {
      const result = await amazonInventoryApi.globalSearch(searchText, WORKSPACE);
      setSearchResult(result);
    } finally {
      setBusy(false);
    }
  };

  const loadItem = async (id: string) => {
    setSelectedId(id);
    try {
      const [it, recommendations, fcst, repl] = await Promise.all([
        amazonInventoryApi.getItem(id),
        amazonInventoryApi.recommendations(id),
        amazonInventoryApi.generateForecast(id),
        amazonInventoryApi.replenishmentPlan(id),
      ]);
      setItem(it);
      setRecs(recommendations);
      setForecast(fcst);
      setPlan(repl);
    } catch {
      setItem(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = async (action: string) => {
    if (selectedIds.size === 0) return;
    setBusy(true);
    try {
      await amazonInventoryApi.triggerBulk({
        action,
        item_ids: [...selectedIds],
        workspace_id: WORKSPACE,
      });
      setSelectedIds(new Set());
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const displayRows: InventoryGridRow[] = searchResult?.items?.length
    ? searchResult.items.map((i) => ({
        id: i.id,
        asin: i.identifiers.asin,
        sku: i.identifiers.sku,
        marketplace_id: i.marketplace_id,
        channel: i.channel,
        sellable: i.quantities.sellable ?? i.quantities.available,
        available: i.quantities.available,
        inbound: i.quantities.inbound ?? 0,
        coverage_days: i.health?.coverage_days ?? 0,
        stockout_risk: i.health?.stockout_risk ?? false,
        warehouse_id: i.warehouse?.fulfillment_center_id,
        updated_at: i.updated_at,
      }))
    : rows.filter((r) => !stockoutFilter || r.stockout_risk);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventory Intelligence</h1>
          <p className="mt-1 text-sm text-slate-600">
            Unified inventory visibility, forecasting, replenishment, and AI-driven optimization
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
        >
          Refresh
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div>
      )}

      {dashboard && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          {[
            { label: 'Items', value: dashboard.total_items },
            { label: 'Active', value: dashboard.active_items },
            { label: 'Sellable', value: dashboard.total_sellable.toLocaleString() },
            { label: 'Stockouts', value: dashboard.stockout_count },
            { label: 'Indexed', value: dashboard.indexed_items },
            { label: 'Alerts', value: dashboard.unread_alerts },
          ].map((c) => (
            <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase text-slate-400">{c.label}</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {health && (
        <section className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
          <h2 className="font-semibold text-emerald-900">Inventory Health Center</h2>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-emerald-800">
            <span>Overall: {Math.round(health.overall_health * 100)}%</span>
            <span>Stockout rate: {Math.round(health.stockout_rate * 100)}%</span>
            <span>Overstock rate: {Math.round(health.overstock_rate * 100)}%</span>
            <span>Avg coverage: {health.avg_coverage_days.toFixed(1)} days</span>
          </div>
        </section>
      )}

      {alerts.length > 0 && (
        <section className="rounded-xl border border-red-200 bg-red-50/50 p-4">
          <h2 className="font-semibold text-red-900">Inventory Alerts ({alerts.length})</h2>
          <ul className="mt-2 space-y-1 text-sm text-red-800">
            {alerts.slice(0, 5).map((a) => (
              <li key={a.id}>
                <strong>{a.title}</strong> — {a.body}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Global search (SKU, ASIN, FNSKU…)"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          className="min-w-[240px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <button
          type="button"
          disabled={busy}
          onClick={runSearch}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          Search
        </button>
        <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <input type="checkbox" checked={stockoutFilter} onChange={(e) => setStockoutFilter(e.target.checked)} />
          Stockout risk only
        </label>
        {BULK_ACTIONS.map((action) => (
          <button
            key={action}
            type="button"
            disabled={busy || selectedIds.size === 0}
            onClick={() => runBulk(action)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium capitalize disabled:opacity-40"
          >
            Bulk {action.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 space-y-4">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3 flex justify-between">
              <h2 className="font-semibold">Enterprise Inventory Grid</h2>
              <span className="text-sm text-slate-500">{searchResult ? searchResult.total : total} items</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 w-8" />
                    <th className="px-3 py-2">SKU / ASIN</th>
                    <th className="px-3 py-2">Channel</th>
                    <th className="px-3 py-2">Sellable</th>
                    <th className="px-3 py-2">Coverage</th>
                    <th className="px-3 py-2">Risk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {displayRows.map((row) => (
                    <tr
                      key={row.id}
                      className={`cursor-pointer hover:bg-slate-50 ${selectedId === row.id ? 'bg-brand-50' : ''}`}
                      onClick={() => loadItem(row.id)}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)} />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.sku ?? row.asin ?? '—'}
                        {row.warehouse_id && <p className="text-slate-400">{row.warehouse_id}</p>}
                      </td>
                      <td className="px-3 py-2">{row.channel}</td>
                      <td className="px-3 py-2">{row.sellable}</td>
                      <td className="px-3 py-2">{row.coverage_days.toFixed(1)}d</td>
                      <td className="px-3 py-2">
                        {row.stockout_risk ? (
                          <span className="text-red-600 font-medium">Stockout</span>
                        ) : (
                          <span className="text-emerald-600">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {displayRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        No inventory items. Create items via API or import.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {searchResult?.took_ms != null && (
              <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
                Search took {searchResult.took_ms}ms
              </div>
            )}
          </div>

          {warehouses.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="font-semibold">Warehouse Views</h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {warehouses.map((w) => (
                  <div key={w.fulfillment_center_id} className="rounded-lg border border-slate-100 p-3 text-sm">
                    <p className="font-medium">{w.fulfillment_center_id}</p>
                    <p className="text-slate-500">
                      {w.item_count} items · {w.total_sellable} sellable · {w.stockout_count} stockouts
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="space-y-4">
          {item ? (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
              <h2 className="font-semibold">Inventory Details</h2>
              <p className="font-mono text-sm">{item.identifiers.sku ?? item.identifiers.asin}</p>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <dt className="text-slate-500">Sellable</dt>
                <dd>{item.quantities.sellable ?? item.quantities.available}</dd>
                <dt className="text-slate-500">Inbound</dt>
                <dd>{item.quantities.inbound ?? 0}</dd>
                <dt className="text-slate-500">Channel</dt>
                <dd>{item.channel}</dd>
                <dt className="text-slate-500">Warehouse</dt>
                <dd>{item.warehouse?.fulfillment_center_id ?? '—'}</dd>
              </dl>
              {forecast && (
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <p className="font-medium">Demand Forecast</p>
                  <p>{forecast.forecast_units.toFixed(1)} units · {forecast.trend ?? 'stable'}</p>
                </div>
              )}
              {plan && (
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <p className="font-medium">Replenishment Plan</p>
                  <p>Safety stock: {plan.safety_stock} · Reorder: {plan.reorder_point}</p>
                  <p>Recommended order: {plan.recommended_order_qty}</p>
                </div>
              )}
              {recs.length > 0 && (
                <div>
                  <p className="text-sm font-medium">AI Recommendations</p>
                  <ul className="mt-1 space-y-1 text-xs text-slate-600">
                    {recs.map((r) => (
                      <li key={r.id}>{r.title}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
              Select an inventory item to view forecast, replenishment, and AI recommendations
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="font-semibold">Activity Timeline</h2>
            <ul className="mt-2 max-h-64 space-y-2 overflow-y-auto text-xs text-slate-600">
              {activity.map((a) => (
                <li key={a.id}>
                  <span className="text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                  <br />
                  {a.message}
                </li>
              ))}
              {activity.length === 0 && <li>No recent activity</li>}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
